import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, dictionaryService, FormLanguage, SmsSendResponse, SmsVerifyResponse, toApiError } from '../services/dictionaryService';
import { useLanguage } from '../i18n/LanguageContext';

interface SmsVerificationDialogProps {
    open: boolean;
    token: string;
    /**
     * Wywoływane po poprawnym kodzie z jednorazowym dowodem weryfikacji — rodzic wykonuje
     * właściwy zapis formularza. Rzucony błąd (np. 403 signature_required) dialog pokaże
     * użytkownikowi i pozwoli wysłać kod ponownie.
     */
    onSigned: (proof: string) => Promise<void>;
    onClose: () => void;
    /** Skąd wysyłać/weryfikować kod — domyślnie endpointy formularza; strona podpisu umowy podaje własne. */
    sendCode?: (token: string, language: FormLanguage) => Promise<SmsSendResponse>;
    verifyCode?: (token: string, code: string) => Promise<SmsVerifyResponse>;
}

type Phase = 'sending' | 'enter' | 'verifying' | 'submitting' | 'locked';

const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Dialog podpisu kodem SMS: wysyła kod przy otwarciu, odlicza ważność i cooldown
 * ponownej wysyłki, weryfikuje kod i przekazuje dowód do rodzica.
 */
const SmsVerificationDialog = ({
    open, token, onSigned, onClose,
    sendCode: sendCodeFn = dictionaryService.sendSmsCode,
    verifyCode: verifyCodeFn = dictionaryService.verifySmsCode,
}: SmsVerificationDialogProps) => {
    const { lang, t } = useLanguage();
    const [phase, setPhase] = useState<Phase>('sending');
    const [phoneMasked, setPhoneMasked] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [expiresAt, setExpiresAt] = useState<number | null>(null);
    const [resendAt, setResendAt] = useState<number | null>(null);
    const [now, setNow] = useState(() => Date.now());
    const sentOnce = useRef(false);

    const describeError = useCallback((e: ApiError): string => {
        switch (e.error) {
            case 'sms_code_invalid':
                return t('sms.attemptsLeft', { count: e.remainingAttempts ?? 0 });
            case 'sms_code_expired':
                return t('sms.expired');
            case 'sms_locked':
                return t('sms.locked');
            case 'sms_limit_exceeded':
                return t('sms.limit', { minutes: Math.max(1, Math.ceil((e.retryAfterSeconds ?? 60) / 60)) });
            case 'sms_cooldown':
                return t('sms.cooldown', { seconds: e.retryAfterSeconds ?? 30 });
            case 'sms_send_failed':
                return t('sms.sendFailed');
            case 'phone_missing':
                return t('sms.phoneMissing');
            case 'signature_required':
            case 'sms_code_not_requested':
                return t('sms.proofExpired');
            case 'consent_required':
                return t('sms.consentRequired');
            case 'too_many_requests':
                return t('sms.tooMany');
            case 'form_outdated':
            case 'incorrect_token':
            case 'form_already_submitted':
                return t('sms.formUnavailable');
            default:
                return t('sms.genericError');
        }
    }, [t]);

    const sendCode = useCallback(async () => {
        setPhase('sending');
        setError(null);
        setCode('');
        try {
            const res = await sendCodeFn(token, lang);
            const started = Date.now();
            setPhoneMasked(res.phoneMasked);
            setExpiresAt(started + res.expiresInSeconds * 1000);
            setResendAt(started + res.resendAfterSeconds * 1000);
            setPhase('enter');
        } catch (err) {
            const apiError = toApiError(err);
            setError(describeError(apiError));
            if (apiError.error === 'sms_locked') {
                setPhase('locked');
            } else {
                // Zostawiamy użytkownika w dialogu — może ponowić wysyłkę po cooldownie.
                setPhase('enter');
                if (apiError.retryAfterSeconds) {
                    setResendAt(Date.now() + apiError.retryAfterSeconds * 1000);
                }
            }
        }
    }, [token, lang, describeError, sendCodeFn]);

    // Pierwsza wysyłka przy otwarciu; reset stanu przy zamknięciu.
    useEffect(() => {
        if (open && !sentOnce.current) {
            sentOnce.current = true;
            sendCode();
        }
        if (!open) {
            sentOnce.current = false;
            setPhase('sending');
            setCode('');
            setError(null);
            setExpiresAt(null);
            setResendAt(null);
        }
    }, [open, sendCode]);

    // Zegar do odliczania ważności kodu i cooldownu.
    useEffect(() => {
        if (!open) return;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [open]);

    const secondsLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0;
    const resendIn = resendAt ? Math.max(0, Math.ceil((resendAt - now) / 1000)) : 0;
    const codeExpired = phase === 'enter' && expiresAt !== null && secondsLeft === 0;
    const busy = phase === 'sending' || phase === 'verifying' || phase === 'submitting';
    const canConfirm = phase === 'enter' && !codeExpired && code.replace(/\s/g, '').length === 6;

    const confirm = async () => {
        if (!canConfirm) return;
        setPhase('verifying');
        setError(null);
        let proof: string;
        try {
            const res = await verifyCodeFn(token, code.replace(/\s/g, ''));
            proof = res.proof;
        } catch (err) {
            const apiError = toApiError(err);
            setError(describeError(apiError));
            setPhase(apiError.error === 'sms_locked' ? 'locked' : 'enter');
            if (apiError.error === 'sms_code_expired') setExpiresAt(Date.now());
            return;
        }
        setPhase('submitting');
        try {
            await onSigned(proof);
            // Rodzic zamyka dialog po udanym zapisie.
        } catch (err) {
            const apiError = toApiError(err);
            setError(describeError(apiError));
            setPhase('enter');
            // Dowód jest jednorazowy — po nieudanym zapisie potrzebny jest nowy kod.
            setExpiresAt(Date.now());
        }
    };

    const statusLine = () => {
        if (phase === 'sending') return t('sms.sending');
        if (phase === 'verifying') return t('sms.verifying');
        if (phase === 'submitting') return t('sms.submitting');
        return null;
    };

    return (
        // Modal właściwy: kliknięcie w tło ani Escape nie zamykają — tylko przycisk „Anuluj",
        // inaczej przypadkowe kliknięcie przerywało wpisywanie kodu i gubiło stan dialogu.
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                if (!busy) onClose();
            }}
            disableEscapeKeyDown
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle>{t('sms.title')}</DialogTitle>
            <DialogContent>
                {phase !== 'locked' && phoneMasked && (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('sms.sentTo', { phone: phoneMasked })}
                    </Typography>
                )}

                {phase !== 'locked' && (
                    <TextField
                        autoFocus
                        fullWidth
                        label={t('sms.codeLabel')}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, '').slice(0, 7))}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
                        disabled={busy || codeExpired}
                        inputProps={{
                            inputMode: 'numeric',
                            autoComplete: 'one-time-code',
                            style: { letterSpacing: '0.3em', fontSize: '22px', textAlign: 'center' },
                        }}
                        sx={{ mb: 1 }}
                    />
                )}

                {phase === 'enter' && expiresAt !== null && (
                    <Typography variant="body2" color={codeExpired ? 'error' : 'textSecondary'} sx={{ mb: 1 }}>
                        {codeExpired ? t('sms.expired') : t('sms.expiresIn', { time: formatTime(secondsLeft) })}
                    </Typography>
                )}

                {statusLine() && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">{statusLine()}</Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity={phase === 'locked' ? 'error' : 'warning'} sx={{ mt: 1 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button onClick={onClose} disabled={busy} color="inherit">
                    {phase === 'locked' ? t('sms.cancel') : t('sms.cancel')}
                </Button>
                {phase !== 'locked' && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={sendCode} disabled={busy || resendIn > 0} variant="outlined">
                            {resendIn > 0 ? t('sms.resendIn', { seconds: resendIn }) : t('sms.resend')}
                        </Button>
                        <Button
                            onClick={confirm}
                            disabled={!canConfirm}
                            variant="contained"
                            sx={{ backgroundColor: '#00796B', '&:hover': { backgroundColor: '#005A4F' } }}
                        >
                            {t('sms.confirm')}
                        </Button>
                    </Box>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SmsVerificationDialog;
